import { CircleAlertIcon, CircleCheckIcon, InfoIcon } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useSelector } from 'react-redux';
import { Toaster as Sonner } from 'sonner';

import { cn } from '@/lib/utils';
import { selectToastEnabled } from '@/redux/uiConfig/selectors';

type ToasterProps = React.ComponentProps<typeof Sonner>;

const TOAST_CLASSES = cn(
  'group-[.toaster]:bg-background group-[.toaster]:text-foreground',
  'group-[.toaster]:border-border group-[.toaster]:shadow-lg',
  '!flex !flex-wrap !items-start !justify-end !gap-x-2 !gap-y-4',
  '!rounded-lg !border-l-4 !py-4 !pr-4 !pl-3'
);

const BUTTON_CLASSES = cn(
  '!ms-0 !h-6 !min-w-11 !rounded-md !px-2 !py-1',
  '!text-xs !leading-4 !font-semibold'
);

const ICON_CLASSES = '!ms-0 !-me-0.5 !h-5 !w-4 !items-center !justify-center';

const CONTENT_CLASSES = '!basis-[calc(100%_-_1.5rem)]';

const TITLE_CLASSES = '!line-clamp-2 !text-sm !leading-5 !font-medium !text-foreground';

const DESCRIPTION_CLASSES = cn(
  '!line-clamp-2 !text-sm !leading-5 !font-normal',
  '!text-foreground-muted !opacity-90'
);

const ACTION_BUTTON_CLASSES = cn(
  BUTTON_CLASSES,
  '!bg-surface-button-primary !text-text-primary-foreground'
);

const CANCEL_BUTTON_CLASSES = cn(
  BUTTON_CLASSES,
  '!bg-surface-button-secondary !text-surface-button-primary'
);

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = 'system' } = useTheme();
  const toastEnabled = useSelector(selectToastEnabled);

  // Don't render toaster if all toast types are disabled
  const anyToastEnabled = Object.values(toastEnabled).some((enabled) => enabled);
  if (!anyToastEnabled) {
    return null;
  }

  return (
    <Sonner
      theme={theme as ToasterProps['theme']}
      className="toaster group"
      icons={{
        success: <CircleCheckIcon size={16} />,
        error: <CircleAlertIcon size={16} />,
        warning: <CircleAlertIcon size={16} />,
        info: <InfoIcon size={16} />,
      }}
      toastOptions={{
        classNames: {
          toast: TOAST_CLASSES,
          icon: ICON_CLASSES,
          content: CONTENT_CLASSES,
          title: TITLE_CLASSES,
          description: DESCRIPTION_CLASSES,
          actionButton: ACTION_BUTTON_CLASSES,
          cancelButton: CANCEL_BUTTON_CLASSES,
          success: cn(
            '!border-l-successful [&_svg]:!stroke-successful',
            !toastEnabled.success && '!hidden'
          ),
          error: cn(
            '!border-l-destructive [&_svg]:!stroke-destructive',
            !toastEnabled.error && '!hidden'
          ),
          warning: cn(
            '!border-l-warning [&_svg]:!stroke-warning',
            !toastEnabled.warning && '!hidden'
          ),
          info: cn(
            '!border-l-informational [&_svg]:!stroke-informational',
            !toastEnabled.info && '!hidden'
          ),
        },
      }}
      style={
        {
          '--width': '384px',
          '--normal-bg': 'var(--color-background)',
          '--normal-text': 'var(--color-foreground)',
          '--normal-border': 'var(--color-stroke)',
        } as React.CSSProperties
      }
      {...props}
    />
  );
};

export { Toaster };
